import generateID from '../generate-id';
import { ShapeType } from '@/services/shape';
import {
  findResAst,
  findResImagePath,
  getColorGroupTable,
  getImageObject,
  getLayerRecolorEnabled,
  getRefName
} from './utils';
import { ImageLayerHandler } from './layout/ImageLayerHandler';
import { ImageArrayLayerHandler } from './layout/ImageArrayLayerHandler';
import { SpriteLayerHandler } from './layout/SpriteLayerHandler';
import { DataItemPointerLayerHandler } from './layout/DataItemPointerLayerHandler';
import { DataItemImageValuesLayerHandler } from './layout/DataItemImageValuesLayerHandler';
import { DataItemImageNumberLayerHandler } from './layout/DataItemImageNumberLayerHandler';
import { DataItemArcProgressBarLayerHandler } from './layout/DataItemArcProgressBarLayerHandler';
import { DataItemLineProgressBarLayerHandler } from './layout/DataItemLineProgressBarLayerHandler';
import { DataItemTextLayerHandler } from './layout/DataItemTextLayerHandler';
import { WidgetLayerHandler } from './layout/WidgetLayerHandler';
import { SlotLayerHandler } from './layout/SlotLayerHandler';
import { FileLayerHandler } from './layout/FileLayerHandler';
import { TranslationHandler } from './layout/TranslationHandler';
import { toRgba } from '@/watch-renderer/utils/color';

const layerAttrsHandler = {
  Image: ImageLayerHandler,
  ImageArray: ImageArrayLayerHandler,
  Sprite: SpriteLayerHandler,
  SpriteComplex: SpriteLayerHandler,
  DataItemPointer: DataItemPointerLayerHandler,
  DataItemImageValues: DataItemImageValuesLayerHandler,
  DataItemImageNumber: DataItemImageNumberLayerHandler,
  DataItemArcProgressBar: DataItemArcProgressBarLayerHandler,
  DataItemLineProgressBar: DataItemLineProgressBarLayerHandler,
  DataItemText: DataItemTextLayerHandler,
  Widget: WidgetLayerHandler,
  Slot: SlotLayerHandler,
  File: FileLayerHandler,
  Translation: TranslationHandler
};

/**
 * 生成图层数据:
 * @param resAst
 * @param layout
 * @returns
 */
function getLayerItem(
  resAst: Ast,
  layout: Ast,
  colors: StoreThemeColorItem[]
): StoreLayerItem | undefined {
  const layerAst = findResAst(resAst, getRefName(layout.attrsMap.ref))[0];
  if (!layerAst) return;
  const { children, tag } = layerAst;
  const _nameLayout = layout.attrsMap._name ?? '未知图层';
  const layer: StoreLayerItem = {
    id: generateID(),
    type: tag,
    attrs: {
      name: _nameLayout,
      x: Math.round(Number(layout.attrsMap.x ?? 0)),
      y: Math.round(Number(layout.attrsMap.y ?? 0)),
      w: Math.round(Number(layout.attrsMap._w)),
      h: Math.round(Number(layout.attrsMap._h)),
      rotation: 0,
      _visibility: true,
      _isLocked: false,
      // 为保证和1.5编辑器一样，这里不在改为2.0数据源了，1.5用单单位这里就用单单位
      // source: getParentSourceKey(layerAst.attrsMap.source),
      source: layerAst.attrsMap.source,
      sourceEnabled: !!layerAst.attrsMap.source,
      recolorEnable: true,
      '_id1.5': layout.attrsMap._id
    },
    children: []
  };
  // 单独处理widget名称
  if (layerAst.tag == 'Widget') {
    const _nameLayer = layerAst.attrsMap._name;
    const _widgetName_zh_CN = layerAst.attrsMap._widgetName_zh_CN;
    if (_nameLayer && _nameLayer != '无') {
      layer.attrs.name = _nameLayer;
    } else if (_widgetName_zh_CN) {
      layer.attrs.name = _widgetName_zh_CN;
    }
  }
  const handler = layerAttrsHandler[tag as keyof typeof layerAttrsHandler];
  if (handler) handler(resAst, layerAst, layer, colors);

  // 处理children
  if (children.length > 0 && tag !== 'ImageArray') {
    layer.children = children
      .map((item) => getLayerItem(resAst, item, colors))
      .filter((item) => !!item) as StoreLayerItem[];
  }

  if (layer.type == ShapeType.Widget) {
    const _getActuallySize = (layer: StoreLayerItem) => {
      let minX = 0;
      let minY = 0;
      let maxX = -Infinity;
      let maxY = -Infinity;
      layer.children?.forEach((kid) => {
        const { x: offsetX = 0, y: offsetY = 0, align } = kid.attrs;
        const { w: width = 0, h: height = 0 } =
          kid.type == ShapeType.Widget ? _getActuallySize(kid) : kid.attrs;
        let _offsetX = offsetX;
        if (kid.type == ShapeType.ImageArray && align) {
          _offsetX -=
            align == 'center' ? width / 2 : align == 'right' ? width : 0;
        }
        minX = Math.min(minX, _offsetX);
        minY = Math.min(minY, offsetY);
        maxX = Math.max(maxX, _offsetX + width);
        maxY = Math.max(maxY, offsetY + height);
      });
      const w = maxX == -Infinity ? 100 : maxX - minX;
      const h = maxY == -Infinity ? 100 : maxY - minY;
      return {
        w,
        h
      };
    };
    // 如果是自动布局，需要反转children
    if (layer.attrs.isFlex) {
      layer.children?.reverse();
    }
    if (!layer.attrs.isFlex && (!layer.attrs.w || !layer.attrs.h)) {
      // 非自动布局下重置w h
      const { w, h } = _getActuallySize(layer);
      // 获取widget的宽高
      layer.attrs.w = w;
      layer.attrs.h = h;
    }
  }

  // 如果是Slot
  if (layer.type == ShapeType.Slot) {
    // 添加slotType
    if (layer.attrs['slotType'] == undefined)
      layer.attrs['slotType'] = 'widget';
    // 添加默认显示：优先通过 Layout 的 parameter（默认项 widget ref）定位，
    // 兼容旧表盘（无 parameter）时回退到第一个
    const parameter = layout.attrsMap.parameter;
    let defaultId = layer?.children?.[0]?.id;
    if (parameter) {
      const idx = children.findIndex(
        (item) => item.attrsMap.ref === parameter
      );
      if (idx >= 0 && layer.children?.[idx]) {
        defaultId = layer.children[idx].id;
      }
    }
    layer.attrs['widgetIdToDisplay'] = defaultId;
  }

  if (layer.attrs.format) {
    delete layer.attrs.format;
  }

  return layer;
}

/**
 * 处理时间指针图层
 * @param resAst
 * @param layouts
 * @param colors
 * @returns
 */
function getTimePointerLayers(
  resAst: Ast,
  layouts: Ast[],
  colors: StoreThemeColorItem[]
): StoreLayerItem[] {
  const resPointerAsts = layouts.map(
    (item) => findResAst(resAst, getRefName(item.attrsMap.ref))[0]
  );
  const resPointersMap = new Map<string, Ast[]>();

  // 分类
  resPointerAsts.forEach((item) => {
    // 忽略没有指针图片的图层
    if (item.attrsMap.ref === undefined) return;
    const key = item.attrsMap.ref.replace(/(_ref\d?|_projectionImg)/, '');
    const arr = resPointersMap.get(key) || [];
    arr.push(item);
    resPointersMap.set(key, arr);
  });

  const _findLayout = (name: string) => {
    return layouts.find((item) => item.attrsMap.ref === `@${name}`);
  };

  const layers: StoreLayerItem[] = [];
  for (const [, value] of resPointersMap) {
    const layerAst0 = value[0];
    const layoutAst0 = _findLayout(layerAst0.attrsMap.name);
    if (!layerAst0) continue;
    const layer: StoreLayerItem = {
      id: generateID(),
      type: ShapeType.Image,
      attrs: {
        name: layoutAst0?.attrsMap._name ?? '未知图层',
        x: Number(layoutAst0?.attrsMap.x),
        y: Number(layoutAst0?.attrsMap.y),
        w: Number(layoutAst0?.attrsMap._w || 0),
        h: Number(layoutAst0?.attrsMap._h || 0),
        rotation: Number(layoutAst0?.attrsMap.rotation || 0),
        _visibility: true,
        sourceEnabled: true,
        source: layerAst0.attrsMap.source,
        pivotX: layerAst0.attrsMap.pivotX,
        pivotY: layerAst0.attrsMap.pivotY
      },
      children: []
    };
    const { attrsMap } = layerAst0;
    const angleRange = +attrsMap.angleRange;
    if (attrsMap.source === 'timeHour') {
      layer.attrs.angleStart = 0;
      layer.attrs.angleEnd = 720;
      layer.attrs.valueStart = 0;
      layer.attrs.valueEnd = 24;
    } else {
      layer.attrs.angleStart = 0;
      layer.attrs.angleEnd = 360;
      layer.attrs.valueStart = 0;
      layer.attrs.valueEnd = 60;
    }
    // 逆光源
    layer.attrs.pointerRegionNum =
      angleRange < 180 ? 4 : angleRange < 360 ? 2 : 1;
    layer.attrs.resources = {
      pointer0: getImageObject(
        resAst,
        getRefName(layerAst0.attrsMap.ref),
        colors
      ),
      pointer1: getImageObject(
        resAst,
        getRefName(
          layer.attrs.pointerRegionNum == 4
            ? layerAst0.attrsMap.ref2
            : layerAst0.attrsMap.ref4
        ),
        colors
      ),
      pointer2: getImageObject(
        resAst,
        getRefName(layerAst0.attrsMap.ref3),
        colors
      ),
      pointer3: getImageObject(
        resAst,
        getRefName(layerAst0.attrsMap.ref4),
        colors
      )
    };

    layer.attrs.recolorEnable = getLayerRecolorEnabled(
      resAst,
      getRefName(layerAst0.attrsMap.ref)
    );
    // 投影
    const projectAst = value.find(
      (item) => item.attrsMap._isProjectionImg === 'true'
    );
    if (projectAst) {
      layer.attrs.resources.pointerShadow = getImageObject(
        resAst,
        getRefName(projectAst.attrsMap.ref),
        colors
      );
      layer.attrs.shadowPointerOffsetY = +attrsMap._projectionOffset;
    }
    // 添加帧率
    if (layerAst0.attrsMap.source == 'timeSecond') {
      layer.attrs['parameter'] = layerAst0.attrsMap['parameter'];
    }
    layers.push(layer);
  }

  return layers;
}

/**
 * 兼容1.5版本，尝试将 manifest.xml 转换为 editor.config.json
 * @param ast watchface 的ast
 * @returns
 */
export function toEditorConfigJson(ast: Ast): StoreEditorConfig {
  const config: StoreEditorConfig = { themes: [] };
  const { children } = ast;
  const resAst = children.find((item) => item.tag === 'Resources');
  if (!resAst) throw new Error('未找到资源节点');

  // 分样式着色表
  const recolorGroupTable: StoreThemeColorItem[] = [];
  const isRecolorEnabled =
    ast.attrsMap._recolorEnable === 'true' && ast.attrsMap.recolorTable;
  if (isRecolorEnabled && ast.attrsMap.recolorTable) {
    recolorGroupTable.push(...getColorGroupTable(ast.attrsMap.recolorTable));
  }

  const themes = children.filter((item) => item.tag === 'Theme');
  let normalThemeIndex = 1;
  let aodThemeIndex = 1;
  themes.forEach((themeAst) => {
    const { attrsMap, children } = themeAst;

    // 当第二个样式时，如果没有layout,则不处理
    if (!children.length && config.themes.length) return;

    const colorGroupTable = isRecolorEnabled
      ? recolorGroupTable
      : attrsMap.colorGroupTable
      ? getColorGroupTable(attrsMap.colorGroupTable)
      : [
          {
            id: generateID(),
            name: '配色1',
            color: '#ffffff'
          }
        ];
    const theme: StoreThemeItem = {
      id: generateID(),
      // attrsMap.name undefined 时息屏名称待修改
      bgColor: toRgba(attrsMap.bgColor ?? ''),
      name:
        attrsMap.type === 'normal'
          ? `样式${normalThemeIndex++}`
          : `样式${aodThemeIndex++}`,
      type: attrsMap.type as 'normal' | 'AOD',
      colorGroupTable,
      children: [],
      preview: findResImagePath(resAst, getRefName(attrsMap.preview))
    };

    // 添加isSingleAOD
    if (theme.type == 'AOD') {
      const themeAODCount = themes.filter(
        (i) => i.attrsMap.type === 'AOD' && i.children.length
      ).length;
      theme.isSingleAOD = themeAODCount == 1;
    }

    // 时间指针
    const timePointers: Ast[] = [];
    const timePointerLayermages: StoreLayerItem[] = [];
    let timeIndex = -1;
    children.forEach((item, index) => {
      if (
        item.attrsMap._tag === 'Time' &&
        item.attrsMap.ref.startsWith('@DataItemPointer_')
      ) {
        timePointers.push(item);
        if (timeIndex < 0) {
          timeIndex = index;
        }
      } else if (
        item.attrsMap._tag === 'Time' &&
        item.attrsMap.ref.startsWith('@Image_')
      ) {
        // 解决在时间类型中，指针轴图片资源图层顺序错误问题
        const layer = getLayerItem(resAst, item, colorGroupTable);
        if (layer) {
          timePointerLayermages?.push(layer);
        }
      } else if (
        item.attrsMap._tag == 'Background' &&
        item.attrsMap.ref == themeAst.attrsMap.bg
      ) {
        // 为背景图片，忽略。下面有专门处理背景图片的逻辑
        return;
      } else {
        const layer = getLayerItem(resAst, item, colorGroupTable);
        if (layer) {
          theme.children?.push(layer);
        }
      }
    });
    if (timePointers.length > 0 || timePointerLayermages.length) {
      const timeLayers = getTimePointerLayers(
        resAst,
        timePointers,
        colorGroupTable
      );
      if (timePointerLayermages.length)
        timeLayers.push(...timePointerLayermages);
      if (timeLayers?.length > 0) {
        theme.children?.splice(timeIndex, 0, ...timeLayers);
      }
    }
    // 处理 bg 属性为图层
    if (attrsMap.bg) {
      const bgLayer = getLayerItem(
        resAst,
        { tag: 'Layout', attrsMap: { ref: attrsMap.bg }, children: [] },
        colorGroupTable
      );
      if (bgLayer) {
        bgLayer.attrs.name = '背景';
        bgLayer.type = ShapeType.Image;
        bgLayer.attrs.x = 0;
        bgLayer.attrs.y = 0;
        bgLayer.attrs.w = Number(ast.attrsMap.width || 454);
        bgLayer.attrs.h = Number(ast.attrsMap.height || 454);

        if (!bgLayer.attrs.resources) bgLayer.attrs.resources = {};
        // 相册/人像表盘背景
        if (attrsMap.isPhotoAlbumWatchface === 'true') {
          bgLayer.type = ShapeType.PhotoAlbumBg;
          bgLayer.attrs.name = '相册/人像表盘背景';

          bgLayer.attrs.resources.photoAlbumBg = getImageObject(
            resAst,
            getRefName(attrsMap.bg),
            colorGroupTable
          );
          if (attrsMap.fg) {
            bgLayer.attrs.resources.photoAlbumFg = getImageObject(
              resAst,
              getRefName(attrsMap.fg),
              colorGroupTable
            );
          }
        } else {
          // 普通背景图
          bgLayer.attrs.resources.pointer0 = getImageObject(
            resAst,
            getRefName(attrsMap.bg),
            colorGroupTable
          );
        }
        if (
          bgLayer.type != ShapeType.Image ||
          resAst.children.find(
            (item) =>
              item.tag == 'Image' && '@' + item.attrsMap.name == attrsMap.bg
          )?.attrsMap?.src
        ) {
          theme.children?.unshift(bgLayer);
        }
      }
    }
    config.themes.push(theme);
  });
  config.i18n = TranslationHandler(ast, resAst);
  return config;
}
